import { AccountSummaryData } from "../AccountSummary.slice";

export const MODAL_ACCOUNT_LIST_MAX_ITEMS = 10;

export type ServiceAccountScope = "multiple" | "current" | "selected";

export const formatAccountLabel = (account: AccountSummaryData) =>
  `${account.accountName} (${account.accountNumber})`;

export const getServiceAccountScope = (
  targetAccounts: AccountSummaryData[],
  activeTenantAccountId?: string
): ServiceAccountScope => {
  if (targetAccounts.length > 1) {
    return "multiple";
  }

  if (
    targetAccounts.length === 1 &&
    activeTenantAccountId &&
    targetAccounts[0].id === activeTenantAccountId
  ) {
    return "current";
  }

  return "selected";
};

export const formatServiceAddressShort = (
  account: AccountSummaryData
): string | null => {
  const { address1, address2, city, state } = account.serviceAddress ?? {};

  if (!address1) {
    return null;
  }

  const street = address2 ? `${address1}, ${address2}` : address1;
  const locality = [city, state].filter(Boolean).join(", ");

  return [street, locality].filter(Boolean).join(", ");
};

export const getVisibleAccounts = <T>(
  accounts: T[],
  maxVisible = MODAL_ACCOUNT_LIST_MAX_ITEMS
) => ({
  visible: accounts.slice(0, maxVisible),
  hiddenCount: Math.max(0, accounts.length - maxVisible),
});

export const formatFollowingServiceAccountsLabel = (count: number) => {
  const accountLabel = count === 1 ? "service account" : "service accounts";

  return `the following ${count} ${accountLabel}`;
};

export const formatOtherServiceAccountsCount = (count: number) => {
  const accountLabel = count === 1 ? "service account" : "service accounts";

  return `${count} other ${accountLabel}`;
};

export const formatAffectedServiceAccountsLabel = (count: number) => {
  const accountLabel = count === 1 ? "service account" : "service accounts";

  return `${count} affected ${accountLabel}`;
};

export const formatTruncatedAccountListSummary = (
  accounts: AccountSummaryData[],
  maxShown = MODAL_ACCOUNT_LIST_MAX_ITEMS
) => {
  const { visible, hiddenCount } = getVisibleAccounts(accounts, maxShown);
  const summary = visible.map(formatAccountLabel).join(", ");

  if (hiddenCount === 0) {
    return summary;
  }

  return `${summary}, and ${hiddenCount} more`;
};
